/**
 * RA-2027 — federal fiscal year quarters for the PBJ export selector.
 *
 * CMS reports PBJ by *federal* fiscal quarter, and FFY Q1 is October–December of the calendar year
 * *before* the fiscal year it belongs to: FFY2026 Q1 is Oct–Dec 2025. Every label this module
 * produces spells the months out for that reason — a bare "Q1 2026" reads as Jan–Mar to anyone who
 * has not memorised the federal calendar, and a customer who files the wrong quarter has filed a
 * bad federal submission.
 *
 *   Q1  Oct–Dec (previous calendar year)
 *   Q2  Jan–Mar
 *   Q3  Apr–Jun
 *   Q4  Jul–Sep
 */

/**
 * PBJ submission became mandatory for the quarter beginning 1 October 2016 — the first day of
 * FFY2017. Nothing earlier is meaningful, so the selector floors here.
 */
export const PBJ_LOWER_BOUND_FOR_FFY = 2017;

export type FfyQuarterNumber = 1 | 2 | 3 | 4;

export interface FfyQuarter {
  /** Federal fiscal year, e.g. 2026. */
  year: number;
  quarter: FfyQuarterNumber;
}

const QUARTER_MONTH_SPANS: Record<FfyQuarterNumber, string> = {
  1: 'Oct–Dec',
  2: 'Jan–Mar',
  3: 'Apr–Jun',
  4: 'Jul–Sep',
};

/** Calendar month index (0 = January) each FFY quarter starts on. */
const QUARTER_START_MONTH: Record<FfyQuarterNumber, number> = { 1: 9, 2: 0, 3: 3, 4: 6 };

/**
 * The calendar year an FFY quarter's months actually fall in — the whole trap this module exists
 * to contain. Only Q1 differs from the fiscal year.
 */
export const calendarYearOfFfyQuarter = ({ year, quarter }: FfyQuarter): number =>
  quarter === 1 ? year - 1 : year;

/** First instant of the quarter, in local time. */
export const ffyQuarterStart = (ffyQuarter: FfyQuarter): Date =>
  new Date(calendarYearOfFfyQuarter(ffyQuarter), QUARTER_START_MONTH[ffyQuarter.quarter], 1);

/** First instant of the *following* quarter — i.e. an exclusive end. */
export const ffyQuarterEndExclusive = (ffyQuarter: FfyQuarter): Date => {
  const start = ffyQuarterStart(ffyQuarter);
  return new Date(start.getFullYear(), start.getMonth() + 3, 1);
};

/**
 * Label for the dropdown, e.g. `Q3 2026 (Apr–Jun)`.
 *
 * Q1 additionally names its calendar year — `Q1 2026 (Oct–Dec 2025)` — because that is the one
 * quarter whose months sit outside the fiscal year it is filed under. Q2–Q4 share their calendar
 * year with the fiscal year, so repeating it there would only add noise.
 */
export const ffyQuarterLabel = ({ year, quarter }: FfyQuarter): string => {
  const months = QUARTER_MONTH_SPANS[quarter];
  return quarter === 1 ? `Q1 ${year} (${months} ${year - 1})` : `Q${quarter} ${year} (${months})`;
};

/** The FFY quarter a calendar date falls in. */
export const ffyQuarterOf = (date: Date): FfyQuarter => {
  const month = date.getMonth();
  if (month >= 9) return { year: date.getFullYear() + 1, quarter: 1 };
  if (month <= 2) return { year: date.getFullYear(), quarter: 2 };
  if (month <= 5) return { year: date.getFullYear(), quarter: 3 };
  return { year: date.getFullYear(), quarter: 4 };
};

export const previousFfyQuarter = ({ year, quarter }: FfyQuarter): FfyQuarter =>
  quarter === 1
    ? { year: year - 1, quarter: 4 }
    : { year, quarter: (quarter - 1) as FfyQuarterNumber };

/**
 * Every quarter that has *started*, newest first, floored at FFY2017 Q1.
 *
 * The in-progress quarter is offered on purpose — a DOR pulling the review CSV mid-quarter to check
 * hours is the normal case. A quarter that has not started yet is not offered at all: there is
 * nothing in it, and a 404 is a worse answer than an absent option.
 */
export const selectableFfyQuarters = (now: Date = new Date()): FfyQuarter[] => {
  const quarters: FfyQuarter[] = [];
  let cursor = ffyQuarterOf(now);
  while (cursor.year >= PBJ_LOWER_BOUND_FOR_FFY) {
    quarters.push(cursor);
    cursor = previousFfyQuarter(cursor);
  }
  return quarters;
};

/**
 * Default selection: the most recently *completed* quarter, which is what gets filed. Clamped to
 * the floor so the default is always an option the selector actually offers.
 */
export const defaultFfyQuarter = (now: Date = new Date()): FfyQuarter => {
  const completed = previousFfyQuarter(ffyQuarterOf(now));
  return completed.year >= PBJ_LOWER_BOUND_FOR_FFY
    ? completed
    : { year: PBJ_LOWER_BOUND_FOR_FFY, quarter: 1 };
};

/** Opaque `<Select>` value — the label is for humans, this is for the form. */
export const ffyQuarterValue = ({ year, quarter }: FfyQuarter): string => `${year}-${quarter}`;

export const parseFfyQuarterValue = (value: string): FfyQuarter | null => {
  const match = /^(\d{4})-([1-4])$/.exec(value);
  if (!match) return null;
  return { year: Number(match[1]), quarter: Number(match[2]) as FfyQuarterNumber };
};
